import React from "react";

export interface PageSectionProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
  background?:
    | "white"
    | "blue-50"
    | "blue-100"
    | "blue-600"
    | "gray-50"
    | "gray-900"
    | "yellow-500"
    | "yellow-600"
    | "citron";
  padding?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "4xl" | "6xl" | "7xl" | "full";
  gradientTop?: boolean;
  gradientBottom?: boolean;
  backgroundImage?: string;
  backgroundSize?: "cover" | "contain" | "auto";
  backgroundPosition?: "center" | "top" | "bottom" | "left" | "right";
  backgroundRepeat?: "no-repeat" | "repeat" | "repeat-x" | "repeat-y";
  overlay?: boolean;
  overlayOpacity?: number;
  overlayColor?: "black" | "white" | "blue" | "gray";
}

const backgroundClasses = {
  white: "bg-white",
  "blue-50": "bg-blue-50",
  "blue-100": "bg-blue-100",
  "blue-600": "bg-blue-600 text-white",
  "gray-50": "bg-gray-50",
  "gray-900": "bg-gray-900 text-white",
  "yellow-500": "bg-yellow-500",
  "yellow-600": "bg-yellow-600",
  citron: "bg-[#C1BB00]",
};

const paddingClasses = {
  sm: "py-8",
  md: "py-12",
  lg: "py-16",
};

const maxWidthClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "4xl": "max-w-4xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
  full: "max-w-full",
};

export function PageSection({
  children,
  className = "",
  id,
  background = "white",
  padding = "lg",
  fullWidth = true,
  maxWidth = "7xl",
  gradientTop = false,
  gradientBottom = false,
  backgroundImage,
  backgroundSize = "cover",
  backgroundPosition = "center",
  backgroundRepeat = "no-repeat",
  overlay = false,
  overlayOpacity = 0.5,
  overlayColor = "black",
}: PageSectionProps) {
  const baseClasses = fullWidth ? "w-full" : "";
  const bgClass = backgroundImage ? "" : backgroundClasses[background];
  const paddingClass = paddingClasses[padding];
  const maxWidthClass = maxWidthClasses[maxWidth];

  // Background image styles
  const backgroundStyles = backgroundImage
    ? {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize,
        backgroundPosition,
        backgroundRepeat,
      }
    : {};

  // Overlay classes and styles
  const getOverlayClass = () => {
    if (!overlay || !backgroundImage) return "";
    switch (overlayColor) {
      case "white":
        return "bg-white";
      case "blue":
        return "bg-blue-900";
      case "gray":
        return "bg-gray-900";
      default:
        return "bg-black";
    }
  };

  const getOverlayStyle = () => {
    if (!overlay || !backgroundImage) return {};
    return { opacity: overlayOpacity };
  };

  // Gradient overlay classes based on background
  const getGradientClass = (position: "top" | "bottom") => {
    if (background === "gray-900") {
      return position === "top"
        ? "before:absolute before:top-0 before:left-0 before:right-0 before:h-16 before:bg-gradient-to-b before:from-white/10 before:to-transparent before:pointer-events-none"
        : "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-16 after:bg-gradient-to-t after:from-white/10 after:to-transparent after:pointer-events-none";
    } else if (background === "blue-600") {
      return position === "top"
        ? "before:absolute before:top-0 before:left-0 before:right-0 before:h-16 before:bg-gradient-to-b before:from-white/20 before:to-transparent before:pointer-events-none"
        : "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-16 after:bg-gradient-to-t after:from-white/20 after:to-transparent after:pointer-events-none";
    } else {
      return position === "top"
        ? "before:absolute before:top-0 before:left-0 before:right-0 before:h-16 before:bg-gradient-to-b before:from-gray-900/10 before:to-transparent before:pointer-events-none"
        : "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-16 after:bg-gradient-to-t after:from-gray-900/10 after:to-transparent after:pointer-events-none";
    }
  };

  const gradientClasses = [
    gradientTop ? getGradientClass("top") : "",
    gradientBottom ? getGradientClass("bottom") : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={`${baseClasses} ${paddingClass} ${bgClass} m-0 relative ${gradientClasses} ${className}`}
      style={backgroundStyles}
      id={id}
    >
      {/* Background overlay */}
      {overlay && backgroundImage && (
        <div
          className={`absolute inset-0 ${getOverlayClass()}`}
          style={getOverlayStyle()}
        />
      )}

      <div
        className={`${maxWidthClass} mx-auto px-4 sm:px-6 lg:px-8 relative z-10`}
      >
        {children}
      </div>
    </section>
  );
}
